import { useEffect, useState } from 'react';
import { subscribeToTicketEvents } from '../utils/ticketEventStream';
import { apiClient } from '../utils/apiClient';

const LAST_TICKET_CHECK_KEY = 'lastTicketCheck';
const TICKET_POLL_INTERVAL_MS = 30000;

// Number of ticket updates since the user last looked at the tickets list: the
// badge on the Tickets nav item (desktop sidebar and mobile tab bar).
//
// The 30s poll is the resync fallback (SSE events are ephemeral and can be
// missed on a dropped connection); the SSE subscription makes it near-instant
// in the normal case. Pass enabled=false to skip all of it - only one of the
// desktop sidebar and the mobile chrome is ever on screen, and the other must
// not run a second poll.
export const useTicketNotificationCount = (pathname, enabled = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!localStorage.getItem(LAST_TICKET_CHECK_KEY)) {
      localStorage.setItem(LAST_TICKET_CHECK_KEY, new Date().toISOString());
    }

    const check = async () => {
      try {
        const since = localStorage.getItem(LAST_TICKET_CHECK_KEY);
        const data = await apiClient.get(`/api/tickets/notifications/count?since=${encodeURIComponent(since)}`);
        setCount(data.count || 0);
      } catch (error) {
        console.error('Error checking ticket activity:', error);
      }
    };

    check();
    const interval = setInterval(check, TICKET_POLL_INTERVAL_MS);
    const unsubscribe = subscribeToTicketEvents((event) => {
      if (['stream-open', 'ticket-created', 'ticket-updated', 'ticket-activity', 'tickets-updated'].includes(event.type)) {
        check();
      }
    });
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [enabled]);

  // Looking at the tickets list clears it.
  useEffect(() => {
    if (enabled && String(pathname || '').toLowerCase().startsWith('/tickets')) {
      localStorage.setItem(LAST_TICKET_CHECK_KEY, new Date().toISOString());
      setCount(0);
    }
  }, [pathname, enabled]);

  return count;
};
